import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AdminProduct,
    GroupPricingStructure,
    PriceCoverage,
    PriceCoverageHole,
    useGetGroupPricingStructureQuery,
    useGetProductsQuery,
} from '../../api/group/groupAdminApiSlice';
import { TenantPayments } from '../../api/account/accountApiTypes';
import PriceScaleTable from '../../components/PriceScaleTable';
import DivideRoomDialog from './components/DivideRoomDialog';
import CombineRoomsDialog from './components/CombineRoomsDialog';
import { combineCandidates, sharedProductRooms } from './combineRoomsRules';
import LocationOfferDialog from './components/LocationOfferDialog';
import EditPriceScalesDialog from './EditPriceScalesDialog';
import RoomProductPricesDialog from './components/RoomProductPricesDialog';
import { PriceCard, priceCardsFor, sharedBeyondCard, withProductsWithoutObjects } from './priceCards';
import {
    DIVISIBLE_USE_CASES,
    TARIFF_USE_CASES,
    isPriceLockedForGroup,
} from './serviceConfigurationRules';

type ServicesPricingSectionProps = {
    /** Use cases the tenant offers — decides which sections render and which
     *  coverage verdict a group is judged by in each. */
    services: string[];
    /** The stored configuration of the tenant IN VIEW — the charging answer
     *  locks the editor on free use cases. Null when nothing is saved. */
    payments: TenantPayments | null;
    /** Editing is scoped to the owner of the tenant on screen; masters on a
     *  foreign tenant and legacy tenants read only. */
    readOnly: boolean;
    /** Divide and combine follow the inventory right, not `readOnly`
     *  (`canReshapeRooms`): a manager may reshape rooms on a read-only page. */
    canReshapeRooms: boolean;
};

/**
 * The price structure per use case — phase 1b of
 * `docs/plans/service-configuration-area.md` — and THE place prices are edited
 * (ADR-0005 amendment 2026-10-21): every card carries "Preise bearbeiten",
 * the tariff editor for SUP/rental/storage cells, the product's room editor for
 * a room product's card. The product list above only says "Bearbeiten".
 *
 * Grouped **by object, with the price category named next to it** (PO,
 * 2026-08-18): one category serves many groups, and an edit to a shared one
 * changes all of them at once, so a shared category is a stated fact on this
 * screen. Rooms and apartments are carded **per product** instead (PO,
 * 2026-10-21; `priceCards.ts`): their price is the product's, so the rooms of
 * one product are one card listing the rooms, not the same table per room.
 *
 * The coverage line is the point, not the display (#2036, ADR-0018 rule 3): a
 * slot no price row covers is not bookable, and until recently the admin path
 * booked exactly those slots at 0,00 €. The verdicts come computed from the
 * backend, where the probe runs through the live matcher — nothing here
 * re-decides what "covered" means.
 *
 */
const ServicesPricingSection = ({ services, payments, readOnly, canReshapeRooms }: ServicesPricingSectionProps) => {
    const { t, i18n } = useTranslation();
    // No cache tag can invalidate this across slices (price edits live in the
    // asset-detail slices), so a fresh mount fetches fresh data — and the
    // tariff editor calls `refetch` after its own save.
    const { data, isLoading, isError, refetch } = useGetGroupPricingStructureQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
    const [editing, setEditing] = useState<GroupPricingStructure | null>(null);
    // A room product's prices: its room editor, opened from its card below.
    const [roomPricing, setRoomPricing] = useState<AdminProduct | null>(null);
    const [dividing, setDividing] = useState<GroupPricingStructure | null>(null);
    // Plan room-houses-and-combining-rooms §4 row 4: the whole room existing
    // rooms are being combined under, with the rooms of its use case.
    const [combining, setCombining] = useState<
        { whole: GroupPricingStructure, service: string } | null
    >(null);
    // Slice 2: a location-sheet create or remove pending confirmation.
    const [sheetAction, setSheetAction] = useState<
        { mode: 'create' | 'remove', group: GroupPricingStructure } | null
    >(null);

    // The room's own image and description, so the divide dialog can show what
    // each part inherits rather than creating it blind. `GroupPricingStructure`
    // carries neither — it is a pricing shape — and this is the existing read
    // that does, so nothing new is added to the API for it.
    const { data: products, isLoading: productsLoading, isError: productsError } = useGetProductsQuery();
    const dividingProduct = useMemo(
        () => (products ?? []).find((p) => p.id === dividing?.productId) ?? null,
        [products, dividing],
    );

    // The rule lives in `serviceConfigurationRules` (pure, tested) rather than
    // here: importing it from this component would drag RTK Query and Auth0
    // into the suite, which is why it had no test before.
    const priceLockedForGroup = (group: GroupPricingStructure): boolean =>
        isPriceLockedForGroup(group.compartmentTypes, payments);

    const groups = useMemo(() => data?.groups ?? [], [data]);

    // How many groups share each price category — a category used by more
    // than one group must be visibly shared (an edit later changes them all).
    const categoryUse = useMemo(() => {
        const counts = new Map<string, number>();
        groups.forEach((g) => {
            if (g.priceCategory) {
                counts.set(g.priceCategory.id, (counts.get(g.priceCategory.id) ?? 0) + 1);
            }
        });
        return counts;
    }, [groups]);

    const groupsForService = (service: string): GroupPricingStructure[] =>
        groups.filter((g) =>
            g.compartmentTypes.some((type) => type.toLowerCase() === service.toLowerCase()),
        );

    // Localized short weekday names, same derivation as PriceScaleTable.
    const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const shortWeekdayNames: Record<string, string> = Object.fromEntries(
        WEEKDAY_KEYS.map((key, i) => [
            key,
            new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
                .format(new Date(Date.UTC(2024, 0, 1 + i))),
        ]),
    );

    const formatHole = (hole: PriceCoverageHole): string => {
        switch (hole.scope) {
            case 'sup_slot':
                return t('services.pricing.hole_sup_slot', {
                    weekday: shortWeekdayNames[hole.weekday ?? ''] ?? hole.weekday,
                    from: hole.fromHour,
                    to: hole.toHour,
                    minutes: hole.durationMinutes,
                });
            case 'missing_nightly':
                return t('services.pricing.hole_missing_nightly');
            case 'missing_weekday_rate':
                return t('services.pricing.hole_missing_weekday_rate');
            case 'missing_weekend_rate':
                return t('services.pricing.hole_missing_weekend_rate');
            case 'no_duration_tier':
                return t('services.pricing.hole_no_duration_tier');
            default:
                return t('services.pricing.hole_no_rows');
        }
    };

    const renderCoverage = (coverage: PriceCoverage | undefined) => {
        // Absent verdict = the backend did not judge this use case; treat it
        // like not_checked rather than inventing a green state.
        const kind = coverage?.kind ?? 'not_checked';
        if (kind === 'ok') {
            return (
                <p className={'text-xs text-foreground/60'}>
                    {t('services.pricing.coverage_ok')}
                </p>
            );
        }
        if (kind === 'not_checked') {
            return (
                <p className={'text-xs text-foreground/60 italic'}>
                    {t('services.pricing.coverage_not_checked')}
                </p>
            );
        }
        // no_rows and holes: unbookable time, stated plainly.
        return (
            <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5 px-3 py-2'}>
                <p className={'text-xs font-medium text-foreground'}>
                    {kind === 'no_rows'
                        ? t('services.pricing.coverage_no_rows')
                        : t('services.pricing.coverage_holes')}
                </p>
                {kind !== 'no_rows' && (
                    <ul className={'mt-1 text-xs text-foreground/80 list-disc pl-4'}>
                        {(coverage?.holes ?? []).map((hole, i) => (
                            <li key={i}>{formatHole(hole)}</li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const renderGroup = (group: GroupPricingStructure, service: string) => {
        const shared = group.priceCategory
            ? (categoryUse.get(group.priceCategory.id) ?? 0) > 1
            : false;
        /*
         * A free use case is NOT locked out of the editor — only out of prices.
         *
         * The rule is "every row prices at zero" (ADR-0018 rule 4), and the
         * backend has always enforced exactly that and no more:
         * `freeUseCaseBlockingWrite` admits any write whose rows are all zero
         * ("Zero rows are what 'free' means in the data — always admissible"),
         * with the note that "the UI lock alone is not a rule". This gate used
         * to add `&& charging !== 'free'`, which was stricter than the rule it
         * mirrored: a free storage tenant was left with the single generated
         * 1440-minute anchor row and no way to offer 30 minutes, 1 hour or
         * 2 hours (PO, KGSt-Forum 2026-09-11). Durations are a tenant decision
         * whether or not money changes hands.
         */
        const editable = !readOnly
            && TARIFF_USE_CASES.has(service.toLowerCase())
            && !!group.priceCategory;
        const priceLocked = priceLockedForGroup(group);
        return (
            <div key={group.id} className={'rounded-[5px] border border-foreground/10 p-4'}>
                <div className={'flex flex-wrap items-baseline gap-x-3 gap-y-1'}>
                    <p className={'text-sm font-semibold text-foreground'}>
                        {group.name ?? (
                            <span className={'italic text-foreground/40'}>
                                {t('common.missing')}
                            </span>
                        )}
                    </p>
                    {group.locationName && (
                        <span className={'text-xs text-foreground/60'}>{group.locationName}</span>
                    )}
                </div>
                <p className={'text-xs text-foreground/60 mt-0.5'}>
                    {group.priceCategory
                        ? t('services.pricing.category_label', { name: group.priceCategory.name })
                        : t('services.pricing.no_category')}
                    {shared && (
                        <span className={'ml-2 rounded-full bg-foreground/10 px-2 py-0.5 text-foreground/70'}>
                            {t('services.pricing.category_shared')}
                        </span>
                    )}
                    {group.rateOrigin === 'product_location' && (
                        <span className={'ml-2 rounded-full bg-foreground/10 px-2 py-0.5 text-foreground/70'}>
                            {t('services.pricing.location_sheet.badge')}
                        </span>
                    )}
                </p>

                <PriceScaleTable
                    priceScales={group.priceScales}
                    useCase={service}
                    priceCategoryId={group.priceCategory?.id}
                />

                <div className={'mt-3 flex flex-col gap-2'}>
                    {renderCoverage(group.coverage[service.toLowerCase()])}

                    {editable && (
                        <div>
                            <button
                                type={'button'}
                                className={'text-sm text-primary hover:underline font-medium'}
                                onClick={() => setEditing(group)}
                            >
                                {t(priceLocked
                                    ? 'services.pricing.edit_durations'
                                    : 'services.pricing.edit_prices')}
                            </button>
                        </div>
                    )}
                    {/* Slice 2: this cell prices from the product's GENERAL
                        list — offer the location sheet (a copy, edited only
                        for this location from then on). Only when the cell
                        knows its product and location; a legacy_group cell
                        migrates first. */}
                    {editable && group.rateOrigin === 'product'
                        && !!group.productId && !!group.locationId && (
                        <div>
                            <button
                                type={'button'}
                                className={'text-sm text-primary hover:underline font-medium'}
                                onClick={() => setSheetAction({ mode: 'create', group })}
                            >
                                {t('services.pricing.location_sheet.create_action')}
                            </button>
                        </div>
                    )}
                    {editable && group.rateOrigin === 'product_location'
                        && !!group.productId && !!group.offerId && (
                        <div>
                            <button
                                type={'button'}
                                className={'text-sm text-primary hover:underline font-medium'}
                                onClick={() => setSheetAction({ mode: 'remove', group })}
                            >
                                {t('services.pricing.location_sheet.remove_action')}
                            </button>
                        </div>
                    )}
                    {/* The free rule (plan §3.1): the zero rows are generated
                        and the prices are not the tenant's to set — but the
                        DURATIONS are, and the backend agrees (a write whose
                        rows are all zero is always admissible). */}
                    {editable && priceLocked && (
                        <p className={'text-xs text-foreground/60'}>
                            {t('services.pricing.free_price_locked')}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    /**
     * A room product's card (PO, 2026-10-21): one price list, edited with the
     * product's room editor, and the rooms it applies to listed below with what
     * is theirs — opening-hours coverage, divide and combine. Prices are never
     * a room's own (#2923), so the editor is offered once per product, not per
     * room.
     */
    const missingName = <span className={'italic text-foreground/40'}>{t('common.missing')}</span>;

    const renderRoomCard = (card: PriceCard, service: string) => {
        const first: GroupPricingStructure | undefined = card.groups[0];
        const product = card.productId
            ? (products ?? []).find((p) => p.id === card.productId) ?? null
            : null;
        // A product without objects has no cell: its own list stands in.
        const priceCategory = first ? first.priceCategory : product?.priceCategory ?? null;
        const priceScales = first ? first.priceScales : product?.priceScales ?? [];
        // The product's editor writes the PRODUCT's list. A cell still pricing
        // from a list of its own is not that list, so it is not offered there.
        const editsThisList = !!product?.priceCategory
            && product.priceCategory.id === priceCategory?.id;
        const divisible = DIVISIBLE_USE_CASES.has(service.toLowerCase());
        return (
            <div key={card.key} className={'rounded-[5px] border border-foreground/10 p-4'}>
                <p className={'text-sm font-semibold text-foreground'}>
                    {/* The product's name only: a room's name never fills a
                        blank one (product refactor phase 2b). A cell without
                        a product is all there is, as in the shared rule. A
                        loading or failed product list says so; it is not a
                        missing name. */}
                    {(() => {
                        if (!card.productId) return first?.name?.trim() || missingName;
                        if (productsLoading) return <span className={'text-foreground/40'}>{t('common.loading')}</span>;
                        if (productsError) return <span className={'italic text-foreground/40'}>{t('services.pricing.product_name_load_error')}</span>;
                        return product?.name?.trim() || missingName;
                    })()}
                </p>
                <p className={'text-xs text-foreground/60 mt-0.5'}>
                    {priceCategory
                        ? t('services.pricing.category_label', { name: priceCategory.name })
                        : t('services.pricing.no_category')}
                    {sharedBeyondCard(card, categoryUse) && (
                        <span className={'ml-2 rounded-full bg-foreground/10 px-2 py-0.5 text-foreground/70'}>
                            {t('services.pricing.category_shared')}
                        </span>
                    )}
                </p>

                <PriceScaleTable
                    priceScales={priceScales}
                    useCase={service}
                    priceCategoryId={priceCategory?.id}
                />
                {priceScales.length === 0 && (
                    <p className={'mt-3 text-sm text-foreground/60'}>{t('services.products.no_prices')}</p>
                )}

                {!readOnly && !productsLoading && (
                    <div className={'mt-3'}>
                        {editsThisList && product ? (
                            <button
                                type={'button'}
                                className={'text-sm text-primary hover:underline font-medium'}
                                onClick={() => setRoomPricing(product)}
                            >
                                {t('services.pricing.edit_prices')}
                            </button>
                        ) : (
                            <p className={'text-xs text-foreground/60'}>
                                {t(product ? 'services.pricing.own_list' : 'services.pricing.no_product')}
                            </p>
                        )}
                    </div>
                )}

                <div className={'mt-4'}>
                    <p className={'text-xs font-medium text-foreground/60'}>
                        {card.groups.length === 0
                            ? t('services.pricing.no_objects')
                            : t('services.pricing.applies_to', { count: card.groups.length })}
                    </p>
                    <ul className={'mt-1 flex flex-col divide-y divide-foreground/5'}>
                        {card.groups.map((group) => (
                            <li key={group.id} className={'py-2 flex flex-col gap-1'}>
                                <div className={'flex flex-wrap items-baseline gap-x-3 gap-y-1'}>
                                    <span className={'text-sm text-foreground'}>
                                        {group.name ?? (
                                            <span className={'italic text-foreground/40'}>{t('common.missing')}</span>
                                        )}
                                    </span>
                                    {group.locationName && (
                                        <span className={'text-xs text-foreground/60'}>{group.locationName}</span>
                                    )}
                                </div>
                                {renderCoverage(group.coverage[service.toLowerCase()])}
                                {/* Divide a room into segments (#1714, D13) and
                                    combine existing rooms under it (§4 row 4) —
                                    per ROOM: whether THIS room qualifies is the
                                    server's answer (`assessDivisibility`), and the
                                    endpoint is addressed by its product id. */}
                                {canReshapeRooms && divisible && group.productId && (
                                    <div>
                                        <button
                                            type={'button'}
                                            className={'text-sm text-primary hover:underline font-medium'}
                                            onClick={() => setDividing(group)}
                                        >
                                            {t('services.divide_room.action')}
                                        </button>
                                        <button
                                            type={'button'}
                                            className={'ml-4 text-sm text-primary hover:underline font-medium'}
                                            onClick={() => setCombining({ whole: group, service })}
                                        >
                                            {t('services.combine_rooms.action')}
                                        </button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <section id={'pricing'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.pricing.title')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {t('services.pricing.description')}
            </p>

            {isLoading ? (
                <div className={'flex justify-center py-10'}>
                    <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
                </div>
            ) : isError ? (
                // An error is never rendered as an empty state — a failed load
                // and "no prices configured" must not look alike.
                <p className={'text-sm text-foreground/60'}>
                    {t('services.pricing.load_error')}
                </p>
            ) : (
                <div className={'flex flex-col gap-6'}>
                    {services.map((service) => {
                        const serviceGroups = groupsForService(service);
                        const tariff = TARIFF_USE_CASES.has(service.toLowerCase());
                        const roomCards = tariff ? [] : withProductsWithoutObjects(
                            priceCardsFor(serviceGroups, true), products ?? [], service,
                        );
                        const empty = tariff ? serviceGroups.length === 0 : roomCards.length === 0;
                        return (
                            <div key={service} className={'flex flex-col gap-3'}>
                                <h3 className={'text-sm font-medium text-foreground'}>
                                    {t(`service_type_selector.${service}`, { defaultValue: service })}
                                </h3>
                                {empty && (
                                    <p className={'text-sm text-foreground/40 italic'}>
                                        {t('services.pricing.no_groups')}
                                    </p>
                                )}
                                {tariff
                                    ? serviceGroups.map((group) => renderGroup(group, service))
                                    : roomCards.map((card) => renderRoomCard(card, service))}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Held back until the products query has SETTLED, not until it
                succeeded. The dialog prefills each part from the room's image
                and description, and opening it before those arrive would show
                "n/a" for an image the room actually has — a false absence, and
                the operator is being asked to review exactly these values.

                A click during that window is not lost: `dividing` is already
                set, so the dialog appears as soon as the query settles. On an
                error it opens with an empty prefill rather than not at all —
                degraded, but an unrelated query must not block the feature. */}
            {dividing && dividing.productId && !productsLoading && (
                <DivideRoomDialog
                    productId={dividing.productId}
                    roomName={dividing.name}
                    // The ROOM's own description and photo (its Group), the
                    // same source the server saves each part from.
                    roomDescription={dividing.description ?? ''}
                    roomImage={dividing.featuredImage ?? null}
                    roomCapacity={dividingProduct?.maxGuests ?? null}
                    taxCategoryId={dividingProduct?.taxCategoryId ?? null}
                    onClose={() => setDividing(null)}
                />
            )}

            {combining && combining.whole.productId && !productsLoading && (
                <CombineRoomsDialog
                    whole={combining.whole}
                    candidates={combineCandidates(combining.whole, groupsForService(combining.service))}
                    sharedRooms={sharedProductRooms(combining.whole, groupsForService(combining.service))}
                    capacityOf={(productId) =>
                        (products ?? []).find((p) => p.id === productId)?.maxGuests ?? null}
                    wholeCapacity={(products ?? [])
                        .find((p) => p.id === combining.whole.productId)?.maxGuests ?? null}
                    onClose={() => setCombining(null)}
                />
            )}

            {sheetAction && (
                <LocationOfferDialog
                    mode={sheetAction.mode}
                    group={sheetAction.group}
                    onClose={() => setSheetAction(null)}
                    onDone={() => refetch()}
                />
            )}

            {/* Keyed per product: the rows seed from its list once. */}
            {roomPricing && (
                <RoomProductPricesDialog
                    key={roomPricing.id}
                    product={roomPricing}
                    payments={payments}
                    onClose={() => setRoomPricing(null)}
                />
            )}

            {editing && (
                <EditPriceScalesDialog
                    group={editing}
                    sharedWithOthers={
                        !!editing.priceCategory
                        && (categoryUse.get(editing.priceCategory.id) ?? 0) > 1
                    }
                    priceLocked={priceLockedForGroup(editing)}
                    onClose={() => setEditing(null)}
                    onSaved={() => refetch()}
                />
            )}
        </section>
    );
};

export default ServicesPricingSection;
